/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import DefalutDashboard from '../../assets/images/dashboard/defalut-dashboard.png';
import i18n from '../../i18n';
import {
  CustomDashboard,
  CustomDashboardInitialProperties,
} from '../../interface/dashboard.interface';
import customDashboardApi from '../../utils/api/custom-dashboard';
import dashboardApi from '../../utils/api/dashboard';

import { setNotificationMessage } from './NotificationManagementSlice';

/**
 * Thunk to fetch custom dashboards.
 * @param query - The query parameters for fetching dashboards.
 */
export const fetchCustomDashboards = createAsyncThunk<CustomDashboard[], any>(
  'custom-dashboard/fetchCustomDashboards',
  async (query, { dispatch }) => {
    try {
      const response = await dashboardApi.getCustomDashboardList(query);
      return response.data.map((item: any) => ({
        ...item,
        id: item._id, // Map backend _id to id
      }));
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message || 'Failed to fetch dashboards',
          code: error?.response?.data?.code || 500,
          status: true,
        }),
      );
      throw error;
    }
  },
);

/**
 * Thunk to fetch facilities hierarchy for dashboard filters.
 */
export const fetchFacilitiesHierarchy = createAsyncThunk<any, void>(
  'custom-dashboard/fetchFacilitiesHierarchy',
  async (_, { dispatch }) => {
    try {
      const response = await customDashboardApi.getDefalutDashboardFilter();
      return response.data;
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message || 'Failed to fetch facilities hierarchy',
          code: error?.response?.data?.code || 500,
          status: true,
        }),
      );
      throw error;
    }
  },
);

/**
 * Thunk to create a custom dashboard.
 * @param data - The dashboard data to create.
 */
export const createCustomDashboard = createAsyncThunk<CustomDashboard, any>(
  'custom-dashboard/createCustomDashboard',
  async (data, { dispatch }) => {
    try {
      const response = await dashboardApi.createCustomDashboard(data);
      dispatch(
        setNotificationMessage({
          type: 'success',
          message: 'Dashboard created successfully',
          status: true,
        }),
      );
      return {
        ...response.data,
        id: response.data._id,
      };
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message || 'Failed to create dashboard',
          code: error?.response?.data?.code || 500,
          status: true,
        }),
      );
      throw error;
    }
  },
);

/**
 * Thunk to update a custom dashboard.
 * @param payload - The update payload containing id and data.
 */
export const updateCustomDashboard = createAsyncThunk<
  CustomDashboard,
  { id: string; data: any; isFileUpload?: boolean }
>(
  'custom-dashboard/updateCustomDashboard',
  async ({ id, data, isFileUpload = false }, { dispatch }) => {
    let requestData = data;
    let useFormData = isFileUpload;

    if (!(data instanceof FormData)) {
      requestData = new FormData();
      Object.keys(data).forEach((key) => {
        requestData.append(key, data[key]);
      });
      useFormData = true;
    }

    try {
      const response = await dashboardApi.updateCustomDashboard(id, requestData, useFormData);
      dispatch(
        setNotificationMessage({
          type: 'success',
          message: 'Dashboard updated successfully',
          status: true,
        }),
      );
      return response?.data?.length === 0
        ? []
        : {
            ...response.data,
            id: response.data._id,
          };
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message || 'Failed to update dashboard',
          code: error?.response?.data?.code || 500,
          status: true,
        }),
      );
      throw error;
    }
  },
);

const { t } = i18n;

const initialState: CustomDashboardInitialProperties = {
  dashboards: [],
  defaultDashboard: {
    id: 'default',
    name: t('defaultDashboard', 'Default Dashboard'),
    isDefault: false,
    showAsTab: true,
    widgets: [],
    signedUrl: DefalutDashboard,
    showTo: 'all',
    updatedAt: '-',
  },
  selectedDashboardId: 'default',
  facilitiesHierarchy: null,
  loading: false,
  error: null,
};

/**
 * Redux slice for managing custom dashboards.
 */
export const customDashboardSlice = createSlice({
  name: 'custom-dashboard',
  initialState,
  reducers: {
    /**
     * Clears the dashboard error state.
     * @param {any} state - The current slice state.
     */
    clearDashboardError: (state) => {
      state.error = null;
    },
    /**
     * Sets the selected dashboard ID.
     * @param {any} state - The current slice state.
     * @param {any} action - The payload containing the dashboard ID.
     */
    setSelectedDashboardId: (state, action: PayloadAction<string>) => {
      state.selectedDashboardId = action.payload;
    },
  },
  /**
   * Handles external actions (async thunks).
   * @param {any} builder - The reducer builder.
   */
  extraReducers: (builder: ActionReducerMapBuilder<CustomDashboardInitialProperties>) => {
    builder
      // Fetch Dashboards
      .addCase(fetchCustomDashboards.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchCustomDashboards.fulfilled,
        (state, action: PayloadAction<CustomDashboard[]>) => {
          state.loading = false;
          state.dashboards = action.payload;
          if (state.defaultDashboard) {
            state.defaultDashboard.isDefault = action.payload.every((d) => !d.isDefault);
          }
        },
      )
      .addCase(fetchCustomDashboards.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch dashboards';
      })
      // Create Dashboard
      .addCase(createCustomDashboard.pending, (state) => {
        state.loading = true;
      })
      .addCase(createCustomDashboard.fulfilled, (state, action: PayloadAction<CustomDashboard>) => {
        state.loading = false;
        state.dashboards.push(action.payload);
        if (state.defaultDashboard) {
          state.defaultDashboard.isDefault = state.dashboards.every((d) => !d.isDefault);
        }
      })
      .addCase(createCustomDashboard.rejected, (state) => {
        state.loading = false;
      })
      // Update Dashboard
      .addCase(updateCustomDashboard.pending, (state) => {
        state.loading = false;
      })
      .addCase(updateCustomDashboard.fulfilled, (state, action: PayloadAction<CustomDashboard>) => {
        state.loading = false;
        const payload = action?.payload as any;
        if (payload?.length === 0) {
          // If no dashboard is default, set system default to true
          if (state.defaultDashboard) {
            state.defaultDashboard.isDefault = true;
          }
          state.dashboards.forEach((d) => {
            d.isDefault = false;
          });
        }
        const index = state.dashboards.findIndex((d) => d.id === action.payload.id);
        if (index !== -1) {
          state.dashboards[index] = action.payload;
        }

        // If the updated dashboard is now default, set all others to false
        if (action.payload.isDefault) {
          state.dashboards.forEach((d) => {
            if (d.id !== action.payload.id) {
              d.isDefault = false;
            }
          });
          if (state.defaultDashboard) {
            state.defaultDashboard.isDefault = false;
          }
        }
      })
      .addCase(updateCustomDashboard.rejected, (state) => {
        state.loading = false;
      })
      // Fetch Facilities Hierarchy
      .addCase(fetchFacilitiesHierarchy.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchFacilitiesHierarchy.fulfilled, (state, action) => {
        state.loading = false;
        state.facilitiesHierarchy = action.payload;
      })
      .addCase(fetchFacilitiesHierarchy.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch facilities hierarchy';
      });
  },
});

export const { clearDashboardError, setSelectedDashboardId } = customDashboardSlice.actions;
export default customDashboardSlice.reducer;
